/*
 * <store-locator-autocomplete> custom element.
 *
 * As-you-type address autofill for the store locator search field. Wraps the
 * search <input> and renders a combobox dropdown of Google Places suggestions
 * (città / CAP / via / zona, restricted to Italy). Picking one resolves the
 * place to exact coordinates and either:
 *
 *   - Full mode: dispatches `store-locator:place-selected`
 *     { lat, lng, label, query }, which the parent <store-locator> turns into a
 *     radius search (see #handlePlaceSelected in store-locator.js).
 *   - Compact mode (`data-redirect-url` set): navigates to the results page
 *     with ?q=&lat=&lng= appended so the full page hydrates straight into
 *     radius mode. Mirrors the <store-locator-geolocate> compact redirect.
 *
 * Suggestions + place details use the new Places library
 * (AutocompleteSuggestion / Place), fetched lazily via the shared
 * `store-locator-maps-loader` on the first keystroke so the Places bundle isn't
 * loaded until the visitor actually types. A single AutocompleteSessionToken
 * groups keystrokes plus the final details fetch into one billed session; it's
 * refreshed after each selection.
 *
 * Degrades to a no-op: with no API key, or when Places fails / returns nothing,
 * no dropdown renders and the existing typed-geocode fallback in
 * <store-locator> still runs. Configuration errors are logged via console.warn.
 *
 * Requires "Places API (New)" enabled on the Google Cloud key — the legacy
 * "Places API" alone yields a 403 API_KEY_SERVICE_BLOCKED on AutocompletePlaces.
 * See imports/Config/README.md.
 */

import { loadMapsApi } from '@theme/store-locator-maps-loader';

const AUTOCOMPLETE_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;
const COORD_PRECISION = 4;
const BLUR_CLOSE_DELAY_MS = 120;
// VIS-298: cache suggestions per normalized input so backspace / re-typing an
// earlier prefix doesn't re-hit the Places API. Bounded to keep memory flat.
const SUGGESTION_CACHE_MAX = 50;

class StoreLocatorAutocomplete extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-store-locator-search-input]');
    this.panel = this.querySelector('[data-store-locator-suggestions-panel]');
    this.list = this.querySelector('[data-store-locator-suggestions]');
    this.apiKey = this.dataset.googleMapsApiKey || '';
    this.redirectUrl = this.dataset.redirectUrl || '';

    // Graceful no-op: without the markup hooks or an API key there's nothing to
    // wire up, and <store-locator>'s typed-geocode fallback still works.
    if (!this.input || !this.panel || !this.list || !this.apiKey) return;

    this.debounceTimer = null;
    this.requestSeq = 0;
    this.placesLib = null;
    this.sessionToken = null;
    this.suggestions = [];
    this.activeIndex = -1;
    this.loadFailed = false;
    this.suggestionCache = new Map();

    this.input.addEventListener('input', this.#handleInput);
    this.input.addEventListener('keydown', this.#handleKeydown);
    this.input.addEventListener('blur', this.#handleBlur);
    // mousedown (not click) so we can preventDefault before the input blurs and
    // tears the panel down out from under the click.
    this.list.addEventListener('mousedown', this.#handleListMousedown);
    this.list.addEventListener('click', this.#handleListClick);
  }

  disconnectedCallback() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (!this.input) return;
    this.input.removeEventListener('input', this.#handleInput);
    this.input.removeEventListener('keydown', this.#handleKeydown);
    this.input.removeEventListener('blur', this.#handleBlur);
    if (this.list) {
      this.list.removeEventListener('mousedown', this.#handleListMousedown);
      this.list.removeEventListener('click', this.#handleListClick);
    }
  }

  // -- Input + suggestion fetching ----------------------------------------

  #handleInput = (event) => {
    const value = event.target.value.trim();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (value.length < MIN_QUERY_LENGTH) {
      this.#clear();
      return;
    }
    this.debounceTimer = setTimeout(() => void this.#fetchSuggestions(value), AUTOCOMPLETE_DEBOUNCE_MS);
  };

  async #ensurePlaces() {
    if (this.placesLib) return this.placesLib;
    await loadMapsApi(this.apiKey);
    const lib = await google.maps.importLibrary('places');
    if (!lib || typeof lib.AutocompleteSuggestion !== 'function') {
      throw new Error('Places library loaded but AutocompleteSuggestion is unavailable');
    }
    this.placesLib = lib;
    return lib;
  }

  async #fetchSuggestions(query) {
    if (this.loadFailed) return;
    const seq = ++this.requestSeq;

    // Serve repeated inputs (backspace, re-typing a prefix) from cache so they
    // don't re-hit the network. The seq bump above already invalidated any
    // in-flight request, so the cached list is the current one.
    const cacheKey = query.trim().toLowerCase();
    if (this.suggestionCache.has(cacheKey)) {
      this.#renderSuggestions(this.suggestionCache.get(cacheKey));
      return;
    }

    let lib;
    try {
      lib = await this.#ensurePlaces();
    } catch (err) {
      this.loadFailed = true;
      console.warn(
        '[store-locator] Could not load the Google Places library: ' +
          (err && err.message ? err.message : err) +
          '. Search autofill disabled; falling back to typed-geocode search.'
      );
      return;
    }
    // The query moved on while the library was loading — drop this response.
    if (seq !== this.requestSeq) return;

    if (!this.sessionToken) this.sessionToken = new lib.AutocompleteSessionToken();

    let response;
    try {
      response = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: this.sessionToken,
        includedRegionCodes: ['it'],
        language: 'it',
        // The `geocode` type collection mirrors the legacy site: città / CAP /
        // via / zona, excluding businesses. (VIS-298 Open Q #1 — verify against
        // the live dropdown with a real key during QA.)
        includedPrimaryTypes: ['geocode'],
      });
    } catch (err) {
      console.warn(
        '[store-locator] Google Places autocomplete request failed: ' +
          (err && err.message ? err.message : err) +
          '. Falling back to typed-geocode search.'
      );
      return;
    }
    // A newer keystroke superseded this request — discard its (now stale) list.
    if (seq !== this.requestSeq) return;

    const placeSuggestions = ((response && response.suggestions) || []).filter((s) => s.placePrediction);
    this.#cacheSuggestions(cacheKey, placeSuggestions);
    this.#renderSuggestions(placeSuggestions);
  }

  #cacheSuggestions(key, suggestions) {
    this.suggestionCache.set(key, suggestions);
    // Map keeps insertion order — evict the oldest entry when over the cap.
    if (this.suggestionCache.size > SUGGESTION_CACHE_MAX) {
      this.suggestionCache.delete(this.suggestionCache.keys().next().value);
    }
  }

  // -- Rendering + open/close ---------------------------------------------

  #renderSuggestions(suggestions) {
    this.suggestions = suggestions;
    this.activeIndex = -1;
    this.list.innerHTML = '';

    if (suggestions.length === 0) {
      this.#close();
      return;
    }

    suggestions.forEach((suggestion, index) => {
      const prediction = suggestion.placePrediction;
      const option = document.createElement('li');
      option.className = 'store-locator__suggestion';
      option.id = `store-locator-suggestion-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.dataset.index = String(index);

      const main = document.createElement('span');
      main.className = 'store-locator__suggestion-main';
      main.textContent = predictionMainText(prediction);
      option.appendChild(main);

      const secondaryText = prediction.secondaryText ? prediction.secondaryText.text : '';
      if (secondaryText) {
        const secondary = document.createElement('span');
        secondary.className = 'store-locator__suggestion-secondary';
        secondary.textContent = secondaryText;
        option.appendChild(secondary);
      }

      this.list.appendChild(option);
    });

    this.#open();
  }

  #open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    this.#syncActiveOption();
  }

  #clear() {
    this.requestSeq += 1; // invalidate any in-flight fetch
    this.suggestions = [];
    this.list.innerHTML = '';
    this.#close();
  }

  get #isOpen() {
    return !this.panel.hidden;
  }

  // -- Keyboard + pointer interaction -------------------------------------

  #handleKeydown = (event) => {
    if (!this.#isOpen || this.suggestions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActive((this.activeIndex + 1) % this.suggestions.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActive((this.activeIndex - 1 + this.suggestions.length) % this.suggestions.length);
        break;
      case 'Enter':
        // Only intercept Enter when an option is highlighted; otherwise let the
        // form submit so the typed-geocode fallback runs.
        if (this.activeIndex >= 0) {
          event.preventDefault();
          void this.#select(this.activeIndex);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.#close();
        break;
      default:
        break;
    }
  };

  #setActive(index) {
    this.activeIndex = index;
    this.#syncActiveOption();
    const option = this.list.children[index];
    if (option) {
      this.input.setAttribute('aria-activedescendant', option.id);
      if (typeof option.scrollIntoView === 'function') {
        option.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  #syncActiveOption() {
    Array.from(this.list.children).forEach((option, index) => {
      option.setAttribute('aria-selected', index === this.activeIndex ? 'true' : 'false');
    });
  }

  #handleListMousedown = (event) => {
    // Keep focus on the input so the option click below can resolve before a
    // blur-driven close removes the option.
    event.preventDefault();
  };

  #handleListClick = (event) => {
    const option = event.target.closest('[role="option"]');
    if (!option) return;
    const index = parseInt(option.dataset.index, 10);
    if (Number.isInteger(index)) void this.#select(index);
  };

  #handleBlur = () => {
    setTimeout(() => this.#close(), BLUR_CLOSE_DELAY_MS);
  };

  // -- Selection ----------------------------------------------------------

  async #select(index) {
    const suggestion = this.suggestions[index];
    if (!suggestion || !suggestion.placePrediction) return;

    const prediction = suggestion.placePrediction;
    const label = prediction.text ? prediction.text.text : this.input.value.trim();

    // Reflect the choice immediately and dismiss the list; the details fetch
    // below resolves the coordinates asynchronously.
    this.input.value = label;
    this.#close();

    let place;
    try {
      place = prediction.toPlace();
      await place.fetchFields({ fields: ['location', 'formattedAddress'] });
    } catch (err) {
      console.warn(
        '[store-locator] Google Places details fetch failed: ' +
          (err && err.message ? err.message : err) +
          '. Submit to fall back to typed-geocode search.'
      );
      return;
    }

    // A details fetch ends the autocomplete session — start a fresh token so the
    // next typing session is billed independently.
    this.sessionToken = null;

    const location = place.location;
    if (!location) return;
    const lat = roundCoord(location.lat());
    const lng = roundCoord(location.lng());
    if (lat == null || lng == null) return;
    const placeLabel = place.formattedAddress || label;

    if (this.redirectUrl) {
      const url = new URL(this.redirectUrl, window.location.origin);
      url.searchParams.set('q', label);
      url.searchParams.set('lat', lat.toFixed(COORD_PRECISION));
      url.searchParams.set('lng', lng.toFixed(COORD_PRECISION));
      window.location.assign(url.toString());
      return;
    }

    this.dispatchEvent(
      new CustomEvent('store-locator:place-selected', {
        detail: { lat, lng, label: placeLabel, query: label },
        bubbles: true,
      })
    );
  }
}

function predictionMainText(prediction) {
  if (prediction.mainText && prediction.mainText.text) return prediction.mainText.text;
  if (prediction.text && prediction.text.text) return prediction.text.text;
  return '';
}

function roundCoord(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.round(value * 1e4) / 1e4;
}

if (!customElements.get('store-locator-autocomplete')) {
  customElements.define('store-locator-autocomplete', StoreLocatorAutocomplete);
}
